"use client";

import { useEffect, useId, useState } from "react";
import { usePathname } from "next/navigation";
import { FiDownload, FiX } from "react-icons/fi";
import { usePwaInstall } from "./pwa/PwaInstallProvider";
import { shouldShowAutoInstallCard } from "../../lib/pwa/autoCardRoutes";
import { getShortInstruction } from "../../lib/pwa/installSteps";

const INSTALL_FALLBACK_DELAY_MS = 2500;
const PWA_INSTALL_BLOCKER_SELECTOR =
  '[data-pwa-install-blocker="true"], [aria-modal="true"], [role="dialog"]';

const PROMPT_MESSAGE = "Use o sistema como app, com acesso rápido pela tela inicial.";

const FOCUS_RING = "focus:outline-none focus:ring-4 focus:ring-primary-100";

function hasActiveInstallBlocker() {
  if (typeof document === "undefined") return false;

  return Boolean(document.querySelector(PWA_INSTALL_BLOCKER_SELECTOR));
}

/**
 * Cartão automático de instalação (CONV-01, CONV-02).
 *
 * Só aparece no admin e na área do cliente, nunca nas páginas públicas. O
 * prompt, a detecção do aparelho, o adiamento de 14 dias e o service worker
 * vivem no `PwaInstallProvider`; aqui fica só a regra de quando mostrar.
 *
 * A instrução fica sempre visível, em qualquer largura (INST-01). Antes, uma
 * regra `@media (max-width: 430px)` num `<style>` inline escondia a instrução
 * justamente no celular.
 */
export function PwaRegistration() {
  const pathname = usePathname();
  const {
    platform,
    isInstalled,
    canPromptNatively,
    hasInstallPath,
    appName,
    autoCardDismissed,
    dismissAutoCard,
    requestInstall,
    openGuide,
  } = usePwaInstall();
  const titleId = useId();
  const [fallbackDelayElapsed, setFallbackDelayElapsed] = useState(false);
  const [hasInstallBlocker, setHasInstallBlocker] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(
      () => setFallbackDelayElapsed(true),
      INSTALL_FALLBACK_DELAY_MS
    );
    return () => window.clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateInstallBlocker = () => {
      setHasInstallBlocker(hasActiveInstallBlocker());
    };

    updateInstallBlocker();

    const observer = new MutationObserver(updateInstallBlocker);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["aria-modal", "data-pwa-install-blocker", "role"],
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  // Com o prompt nativo o cartão aparece na hora; sem ele, só no celular e
  // depois de 2,5 s (CONV-02).
  const ready = canPromptNatively || (fallbackDelayElapsed && platform !== "desktop");

  if (
    !shouldShowAutoInstallCard(pathname) ||
    isInstalled ||
    !hasInstallPath ||
    autoCardDismissed ||
    hasInstallBlocker ||
    !ready
  ) {
    return null;
  }

  // INST-06: quando o prompt chega, a instrução manual dá lugar ao botão.
  const message = canPromptNatively ? PROMPT_MESSAGE : getShortInstruction(platform);
  const primaryClassName = `inline-flex min-h-[44px] items-center justify-center rounded-xl bg-primary-600 px-3 text-sm font-bold text-white shadow-sm transition duration-150 hover:bg-primary-700 ${FOCUS_RING}`;

  return (
    <aside
      aria-labelledby={titleId}
      className="fixed inset-x-3 bottom-[calc(5.25rem+env(safe-area-inset-bottom))] z-[10000] mx-auto max-w-[360px] rounded-2xl border border-slate-200/80 bg-white p-3 shadow-subtle sm:inset-x-auto sm:bottom-4 sm:right-4 sm:w-[360px] sm:max-w-none"
    >
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
          <FiDownload className="h-5 w-5" aria-hidden="true" />
        </div>

        <div className="min-w-0 flex-1 pt-0.5">
          <p
            id={titleId}
            className="m-0 truncate text-sm font-bold leading-5 text-slate-900"
          >
            Instalar {appName}
          </p>
          <p className="m-0 mt-1 text-xs leading-5 text-slate-600 sm:text-sm">
            {message}
          </p>
        </div>

        <button
          type="button"
          onClick={dismissAutoCard}
          className={`-mr-1 -mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-slate-500 transition duration-150 hover:bg-slate-100 hover:text-slate-900 ${FOCUS_RING}`}
          aria-label="Fechar aviso de instalação"
        >
          <FiX className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={dismissAutoCard}
          className={`inline-flex min-h-[44px] items-center justify-center rounded-xl px-3 text-sm font-bold text-slate-600 transition duration-150 hover:bg-slate-100 hover:text-slate-900 ${FOCUS_RING}`}
        >
          Mais tarde
        </button>
        {canPromptNatively ? (
          <button
            type="button"
            onClick={() => void requestInstall()}
            className={primaryClassName}
          >
            Instalar app
          </button>
        ) : (
          <button type="button" onClick={openGuide} className={primaryClassName}>
            Ver como instalar
          </button>
        )}
      </div>
    </aside>
  );
}
